import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaApple,
  FaBolt,
  FaCheckCircle,
  FaClock,
  FaDatabase,
  FaExclamationTriangle,
  FaFileAlt,
  FaGlobe,
  FaHistory,
  FaLaptop,
  FaLinux,
  FaNetworkWired,
  FaPlus,
  FaQuestionCircle,
  FaRobot,
  FaServer,
  FaShieldAlt,
  FaTimes,
  FaWindows,
} from "react-icons/fa";
import { useAuth } from "../../../context/contextAuth.js";
import { useDesign } from "../../../context/contextDesign.js";
import { useNetwork } from "../../../context/Dashboard/contextNetwork.js";
import { useChat } from "../../../context/contextChat.js";
import Loader from "../../../components/loaders/Loader-4";
import ScanFromUUID from "../../../layouts/network/Scan.jsx";
import ScanOS from "../../../layouts/network/ScanOS.jsx";
import ScanInstallSteps from "../../../layouts/network/ScanInstallSteps.jsx";
import "./network-security-center.css";

const SEVERITY_ORDER = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
const RISK_COLORS = ["#ff5c72", "#ff9b51", "#ffd166", "#55d6a0"];
const SENSITIVE_PORTS = new Set([
  21, 22, 23, 25, 53, 110, 135, 139, 445, 1433, 3306, 3389, 5432, 5900,
  6379, 8080, 9200, 11211, 27017,
]);

const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
};

const parseObject = (value, fallback = {}) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
    } catch (_error) {
      return fallback;
    }
  }
  return fallback;
};

const numberOr = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampScore = (value) => Math.min(10, Math.max(0, numberOr(value, 0)));

const severityOf = (finding) => {
  const severity = String(
    finding?.baseSeverity ||
      finding?.severity ||
      finding?.risk ||
      finding?.riskcode ||
      "info"
  ).toLowerCase();

  if (severity.includes("critical")) return "critical";
  if (severity.includes("high")) return "high";
  if (severity.includes("medium") || severity.includes("moderate")) return "medium";
  if (severity.includes("low")) return "low";
  return "info";
};

const titleOf = (finding) =>
  finding?.name ||
  finding?.alert ||
  finding?.title ||
  finding?.cveId ||
  finding?.cve_id ||
  "Security finding";

const cveIdsOf = (finding) => {
  const values = [
    finding?.cveId,
    finding?.cve_id,
    finding?.cve,
    finding?.CVE_ID,
    finding?.codes,
  ]
    .flat()
    .filter(Boolean)
    .join(" ");
  const matches = values.match(/CVE-\d{4}-\d{4,}/gi) || [];
  return [...new Set(matches.map((value) => value.toUpperCase()))];
};

const cveOf = (finding) => cveIdsOf(finding)[0] || null;

const priorityBandOf = (finding) => {
  const bands = { critical: "P0", high: "P1", medium: "P2", low: "P3", info: "P4" };
  return bands[severityOf(finding)] || "P4";
};

const findingMetric = (finding, key) => numberOr(finding?.[key], -1);

const compareFindingRisk = (a, b) => {
  const severityDifference =
    (SEVERITY_ORDER[severityOf(b)] || 0) - (SEVERITY_ORDER[severityOf(a)] || 0);
  if (severityDifference) return severityDifference;

  const exploitabilityDifference =
    findingMetric(b, "exploitability") - findingMetric(a, "exploitability");
  if (exploitabilityDifference) return exploitabilityDifference;

  const impactDifference = findingMetric(b, "impact") - findingMetric(a, "impact");
  if (impactDifference) return impactDifference;

  return titleOf(a).localeCompare(titleOf(b));
};

const shortText = (value, maximumLength = 70) => {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "Not reported";
  return text.length > maximumLength
    ? `${text.slice(0, maximumLength - 1)}…`
    : text;
};

const metricLabel = (value) =>
  Number.isFinite(Number(value)) ? String(Number(value)) : "—";

const dateLabel = (value, includeTime = false) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
};

const scanIdentity = (scan, index) =>
  scan?.ip_address || scan?.mac_uuid || scan?.mac_address || scan?.scan_uuid || `scan-${index}`;

const visibilityOf = (scan) => {
  const value = String(
    scan?.scan_type || scan?.visibility || scan?.network_type || ""
  ).toLowerCase();
  if (value.includes("public")) return "public";
  if (value.includes("private")) return "private";
  return "unknown";
};

const osOf = (scan) => {
  const value = String(
    scan?.os || scan?.os_name || scan?.platform || scan?.operating_system || ""
  ).toLowerCase();
  if (value.includes("windows")) return "windows";
  if (value.includes("linux") || value.includes("ubuntu") || value.includes("debian")) return "linux";
  if (value.includes("mac") || value.includes("darwin")) return "macos";
  return "unknown";
};

const servicePort = (service) =>
  service?.Port || service?.port || service?.portid || service?.port_id || null;

const servicePortNumber = (service) => {
  const port = Number.parseInt(String(servicePort(service) || ""), 10);
  return Number.isFinite(port) ? port : -1;
};

const serviceName = (service) =>
  service?.Service || service?.service || service?.name || "Service detected";

const EmptyState = ({ children }) => <div className="nsc-empty">{children}</div>;

const KpiCard = ({ icon, label, value, helper, tone = "blue" }) => (
  <article className={`nsc-kpi nsc-kpi--${tone}`}>
    <span className="nsc-kpi__icon">{icon}</span>
    <div>
      <span className="nsc-kpi__label">{label}</span>
      <strong>{value}</strong>
      <small>{helper}</small>
    </div>
  </article>
);

const NetworkSecurityCenter = () => {
  const { bHeightVal } = useDesign();
  const { userId } = useAuth();
  const { pathname } = useLocation();
  const {
    setPrompt,
    setShowChat,
    handleSecurityActionFor,
    loading: chatLoading,
  } = useChat();
  const {
    initData,
    loadNetworkData,
    networkLoading,
    networkResults,
    currentIndex,
    setCurrentIndex,
    ipVulTable,
    ipEpssTable,
    ipServices,
    ipScanLoading,
    setScanType,
    error,
    setIpDashboardView,
    setIpHeader,
  } = useNetwork();
  const [activePanel, setActivePanel] = useState(null);
  const [findingsView, setFindingsView] = useState("prioritized");
  const initializedUserIdRef = useRef(null);
  const workflowPanelRef = useRef(null);

  const results = useMemo(
    () => (Array.isArray(networkResults) ? networkResults : []),
    [networkResults]
  );
  const boundedIndex = results.length ? Math.min(currentIndex, results.length - 1) : 0;
  const selectedScan = results[boundedIndex] || null;
  const findings = useMemo(
    () => parseArray(selectedScan?.vuln_data || ipVulTable),
    [selectedScan, ipVulTable]
  );
  const epssRows = useMemo(
    () => parseArray(selectedScan?.epss_data || ipEpssTable),
    [selectedScan, ipEpssTable]
  );
  const services = useMemo(
    () => parseArray(selectedScan?.services_data || ipServices),
    [selectedScan, ipServices]
  );
  const coverage = useMemo(() => parseObject(selectedScan?.coverage), [selectedScan]);
  const coverageSummary = useMemo(
    () => parseObject(selectedScan?.coverage_summary),
    [selectedScan]
  );
  const diagnostics = useMemo(
    () => parseArray(selectedScan?.diagnostics),
    [selectedScan]
  );
  const comparison = useMemo(
    () => parseObject(selectedScan?.comparison, null),
    [selectedScan]
  );
  const webCandidates = useMemo(
    () => parseArray(selectedScan?.web_candidates),
    [selectedScan]
  );
  const coverageRows = useMemo(
    () => Object.entries(coverage).filter(([, item]) => item?.status),
    [coverage]
  );

  useEffect(() => {
    if (!userId) {
      initializedUserIdRef.current = null;
      return;
    }

    if (initializedUserIdRef.current === userId) return;
    initializedUserIdRef.current = userId;
    initData();
  }, [userId, initData]);

  useEffect(() => {
    if (!ipScanLoading && results.length > 0) {
      if (currentIndex > results.length - 1) setCurrentIndex(results.length - 1);
      loadNetworkData(boundedIndex);
      setIpDashboardView(true);
      setIpHeader(true);
    } else if (!ipScanLoading && results.length === 0) {
      setIpHeader(false);
    }
    // The network context exposes action functions with unstable identities.
    // Re-running this effect for those identities would repeatedly reload the same scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, currentIndex, ipScanLoading, boundedIndex]);

  useEffect(() => {
    if (ipScanLoading) setActivePanel("scan");
  }, [ipScanLoading]);

  const counts = useMemo(() => {
    const fallback = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    findings.forEach((finding) => {
      const severity = severityOf(finding);
      if (Object.prototype.hasOwnProperty.call(fallback, severity)) fallback[severity] += 1;
      else fallback.info += 1;
    });
    return {
      critical: numberOr(selectedScan?.c_vul, fallback.critical),
      high: numberOr(selectedScan?.h_vul, fallback.high),
      medium: numberOr(selectedScan?.m_vul, fallback.medium),
      low: numberOr(selectedScan?.l_vul, fallback.low),
      info: fallback.info,
      total: numberOr(selectedScan?.t_vul, findings.length),
    };
  }, [selectedScan, findings]);

  const score = selectedScan && selectedScan?.score_status !== 'not_calculated' && selectedScan?.ss != null
    ? clampScore(selectedScan.ss)
    : null;
  const scorePercent = score === null ? 0 : score * 10;
  const riskLabel =
    score === null ? "Not calculated" : score >= 8 ? "Low" : score >= 6 ? "Moderate" : score >= 4 ? "High" : "Critical";
  const scoreTone =
    score === null ? "unknown" : score >= 8 ? "good" : score >= 6 ? "watch" : score >= 4 ? "high" : "critical";

  const assetStats = useMemo(() => {
    const unique = new Map();
    results.forEach((scan, index) => unique.set(scanIdentity(scan, index), scan));
    const assets = [...unique.values()];
    return {
      total: assets.length,
      public: assets.filter((asset) => visibilityOf(asset) === "public").length,
      private: assets.filter((asset) => visibilityOf(asset) === "private").length,
      unknownVisibility: assets.filter((asset) => visibilityOf(asset) === "unknown").length,
      knownAgents: new Set(assets.map((asset) => asset.mac_uuid).filter(Boolean)).size,
      os: assets.reduce(
        (acc, asset) => {
          acc[osOf(asset)] += 1;
          return acc;
        },
        { windows: 0, linux: 0, macos: 0, unknown: 0 }
      ),
    };
  }, [results]);

  const visibilityValue = (value) =>
    assetStats.total && assetStats.unknownVisibility === assetStats.total ? "—" : value;

  const trendData = useMemo(
    () =>
      results
        .filter((scan) => scan?.score_status !== 'not_calculated' && scan?.ss != null)
        .map((scan, index) => ({
          scan: `S${index + 1}`,
          score: clampScore(scan.ss),
          timestamp: scan.created_at ? new Date(scan.created_at).getTime() : index,
        }))
        .sort((a, b) => a.timestamp - b.timestamp),
    [results]
  );

  const riskData = [
    { name: "Critical", value: counts.critical },
    { name: "High", value: counts.high },
    { name: "Medium", value: counts.medium },
    { name: "Low", value: counts.low },
  ];
  const hasRiskData = riskData.some((item) => item.value > 0);

  const prioritizedFindings = useMemo(
    () => [...findings].sort(compareFindingRisk),
    [findings]
  );
  const priorityQueue = useMemo(
    () => prioritizedFindings.slice(0, Math.min(6, prioritizedFindings.length)),
    [prioritizedFindings]
  );
  const allFindingRows = useMemo(
    () =>
      findings.map((finding) => ({
        finding,
        priorityIndex: prioritizedFindings.indexOf(finding),
      })),
    [findings, prioritizedFindings]
  );
  const topFinding = prioritizedFindings[0] || null;

  const verifiedCves = useMemo(() => {
    const seen = new Set();
    return epssRows
      .filter((row) => cveOf(row))
      .filter((row) => {
        const id = cveOf(row);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, 4);
  }, [epssRows]);

  const sensitiveServices = useMemo(
    () =>
      services
        .filter((service) => SENSITIVE_PORTS.has(servicePortNumber(service)))
        .slice(0, 5),
    [services]
  );

  const askAi = (question) => {
    if (!selectedScan) {
      setPrompt(`${question}\n\nNo network scan has completed yet.`);
      setShowChat(true);
      return;
    }

    const findingLines = prioritizedFindings.map((finding, index) => {
      const references = cveIdsOf(finding).join(", ") || finding?.codes || "none";
      return [
        `#${index + 1}`,
        priorityBandOf(finding),
        severityOf(finding).toUpperCase(),
        shortText(titleOf(finding), 58),
        `port ${shortText(finding?.port, 18)}`,
        `refs ${shortText(references, 32)}`,
        `exploitability ${metricLabel(finding?.exploitability)}`,
        `impact ${metricLabel(finding?.impact)}`,
      ].join(" | ");
    });
    const context =
      `Assessment ${selectedScan.scan_uuid || `S${boundedIndex + 1}`}; ` +
      `SAVE score ${score ?? "not calculated"}/10; ${counts.total} findings ` +
      `(critical ${counts.critical}, high ${counts.high}, medium ${counts.medium}, low ${counts.low}).`;
    const grounding =
      "Use the complete risk-ranked scanner evidence below. Cite finding numbers and exact titles in recommendations. " +
      "Separate observed evidence from assumptions, do not invent versions or exposure, and avoid generic security-program advice.";

    setPrompt(
      `${question}\n\nCurrent SAVE network context:\n${context}\n\n${grounding}\n\n` +
      `Risk-ranked findings (${prioritizedFindings.length}):\n${findingLines.join("\n")}`
    );
    setShowChat(true);
  };

  const networkFindingContext = (finding, index) => ({
    contextType: "network_finding",
    findingName: titleOf(finding),
    priorityRank: index + 1,
    priorityBand: priorityBandOf(finding),
    severity: severityOf(finding),
    cveId: cveOf(finding) || "",
    codes: finding?.codes || cveIdsOf(finding).join(", "),
    port: finding?.port || "Not reported",
    asset:
      selectedScan?.ip_address ||
      selectedScan?.mac_address ||
      selectedScan?.mac_uuid ||
      "Not reported",
    vulnerabilityType: finding?.type || "Not reported",
    platform: finding?.platform || "Not reported",
    exploitability: finding?.exploitability ?? "Not reported",
    impact: finding?.impact ?? "Not reported",
    scannerSource: finding?.source || "SAVE network scanner",
    scanId: selectedScan?.scan_uuid || `S${boundedIndex + 1}`,
    totalFindings: counts.total,
  });

  const requestFindingRemediation = (finding, index) => {
    handleSecurityActionFor(
      networkFindingContext(finding, index),
      "network_remediation_plan",
      `Generate remediation action plan · ${priorityBandOf(finding)} #${index + 1}`
    );
  };

  const renderFindingAction = (finding, priorityIndex, listName) => {
    const references = cveIdsOf(finding).join(", ") || finding?.codes;
    const safePriorityIndex = priorityIndex >= 0 ? priorityIndex : 0;
    return (
      <button
        className="nsc-finding-action"
        type="button"
        key={`${listName}-${titleOf(finding)}-${finding?.port || "no-port"}-${safePriorityIndex}`}
        onClick={() => requestFindingRemediation(finding, safePriorityIndex)}
        disabled={chatLoading}
        title={`Generate a remediation action plan for ${titleOf(finding)}`}
      >
        <span className={`nsc-finding-rank nsc-finding-rank--${severityOf(finding)}`}>
          {priorityBandOf(finding)}
          <small>#{safePriorityIndex + 1}</small>
        </span>
        <span className="nsc-finding-copy">
          <strong>{titleOf(finding)}</strong>
          <small>
            {finding?.port || "Port not reported"}
            {references ? ` · ${shortText(references, 38)}` : ""}
            {` · E ${metricLabel(finding?.exploitability)}`}
            {` · I ${metricLabel(finding?.impact)}`}
          </small>
        </span>
        <span className={`nsc-finding-severity nsc-finding-severity--${severityOf(finding)}`}>
          <i className={`nsc-severity-dot nsc-severity-dot--${severityOf(finding)}`} />
          {severityOf(finding)}
        </span>
        <span className="nsc-finding-ai"><FaRobot /> Remediate</span>
      </button>
    );
  };

  const openScan = (type) => {
    setScanType(type);
    setActivePanel("scan");
    window.requestAnimationFrame(() => {
      workflowPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const openAgents = () => {
    setActivePanel("agents");
    window.requestAnimationFrame(() => {
      workflowPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const osCards = [
    { id: "windows", label: "Windows", icon: <FaWindows />, count: assetStats.os.windows },
    { id: "linux", label: "Linux", icon: <FaLinux />, count: assetStats.os.linux },
    { id: "macos", label: "macOS", icon: <FaApple />, count: assetStats.os.macos },
    { id: "unknown", label: "Unknown", icon: <FaQuestionCircle />, count: assetStats.os.unknown },
  ];

  if (networkLoading) {
    return (
      <div className="nsc-loading" style={{ height: bHeightVal }}>
        <Loader type="network_vul" />
      </div>
    );
  }

  return (
    <div className="nsc-shell" style={{ height: bHeightVal }}>
      <div className="nsc-page">
        <header className="nsc-header">
          <div>
            <span className="nsc-eyebrow">Network Security Operations</span>
            <h1>Security Command Center</h1>
            <p>Unified visibility across network assets, exposure, findings, and response priorities.</p>
          </div>
          <div className="nsc-header__actions">
            {results.length > 0 && (
              <label className="nsc-scan-select">
                <span>Assessment</span>
                <select value={boundedIndex} onChange={(event) => setCurrentIndex(Number(event.target.value))}>
                  {results.map((scan, index) => (
                    <option key={scan.scan_uuid || index} value={index}>
                      S{index + 1} · {dateLabel(scan.created_at)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Link className="nsc-legacy-link" to={`${pathname}?networkDashboard=legacy`}>
              Legacy dashboard
            </Link>
          </div>
        </header>

        {error && (
          <div className="nsc-alert nsc-alert--error">
            <FaExclamationTriangle /> {error.message || String(error)}
          </div>
        )}

        {activePanel && (
          <section className="nsc-card nsc-workflow-panel" ref={workflowPanelRef}>
            <div className="nsc-section-heading">
              <div>
                <span className="nsc-eyebrow">{activePanel === "scan" ? "Completed Scanner Evidence" : "Agent Management"}</span>
                <h2>{activePanel === "scan" ? "Add a scan to this dashboard" : "Download and install an agent"}</h2>
              </div>
              {!ipScanLoading && <button type="button" className="nsc-icon-button" onClick={() => setActivePanel(null)} aria-label="Close workflow"><FaTimes /></button>}
            </div>
            {activePanel === "scan" ? (
              ipScanLoading ? (
                <div className="nsc-scan-running"><Loader type="network_vul" /><strong>Processing scanner evidence…</strong><span>Keep this page open while SAVE adds the completed scan to your dashboard.</span></div>
              ) : <ScanFromUUID embedded />
            ) : (
              <div className="nsc-agent-layout"><ScanOS /><ScanInstallSteps /></div>
            )}
          </section>
        )}

        <section className="nsc-kpi-grid" aria-label="Network security key performance indicators">
          <KpiCard icon={<FaShieldAlt />} label="Security Score" value={score === null ? "—" : `${score.toFixed(1)}/10`} helper={riskLabel} tone={scoreTone} />
          <KpiCard icon={<FaServer />} label="Total Assets" value={assetStats.total || "—"} helper="Observed identities" />
          <KpiCard icon={<FaGlobe />} label="Public Assets" value={visibilityValue(assetStats.public)} helper={visibilityValue(assetStats.public) === "—" ? "Visibility not reported" : "Observed public"} tone="cyan" />
          <KpiCard icon={<FaNetworkWired />} label="Private Assets" value={visibilityValue(assetStats.private)} helper={visibilityValue(assetStats.private) === "—" ? "Visibility not reported" : "Observed private"} tone="purple" />
          <KpiCard icon={<FaExclamationTriangle />} label="Critical Risks" value={selectedScan ? counts.critical : "—"} helper={selectedScan ? `${counts.total} total findings` : "Awaiting first scan"} tone="critical" />
          <KpiCard icon={<FaClock />} label="Last Scan" value={selectedScan ? dateLabel(selectedScan.created_at) : "—"} helper={selectedScan ? "Selected assessment" : "No scan history"} tone="cyan" />
          <KpiCard icon={<FaLaptop />} label="Active Agents" value="—" helper={`${assetStats.knownAgents} known · heartbeat unavailable`} tone="purple" />
          <KpiCard
            icon={<FaCheckCircle />}
            label="Assessment Coverage"
            value={selectedScan && Number.isFinite(Number(coverageSummary?.percentage)) ? `${coverageSummary.percentage}%` : '—'}
            helper={selectedScan ? `${coverageSummary.completed || 0}/${coverageSummary.planned || 0} planned checks complete` : 'Awaiting first scan'}
            tone={coverageSummary?.complete ? 'good' : 'watch'}
          />
        </section>

        <section className="nsc-command-grid">
          <article className="nsc-card nsc-score-card">
            <div className="nsc-section-heading">
              <div>
                <span className="nsc-eyebrow">Live Security Posture</span>
                <h2>Overall SAVE score</h2>
              </div>
              <span className="nsc-status-pill"><i /> {selectedScan ? "Selected scan" : "Awaiting scan"}</span>
            </div>
            <div className={`nsc-score-orb nsc-score-orb--${scoreTone}`} style={{ "--score": `${scorePercent}%` }}>
              <div className="nsc-score-orb__mesh" />
              <div className="nsc-score-orb__content">
                <span>Security posture</span>
                <strong>{score === null ? "—" : score.toFixed(1)}</strong>
                <small>Risk: {riskLabel}</small>
              </div>
            </div>
            <div className="nsc-score-meta">
              <div><span>Last assessed</span><strong>{selectedScan ? dateLabel(selectedScan.created_at, true) : "Not yet"}</strong></div>
              <div><span>Root findings</span><strong>{selectedScan ? counts.total : "—"}</strong></div>
            </div>
          </article>

          <article className="nsc-card nsc-ai-card">
            <div className="nsc-section-heading">
              <div>
                <span className="nsc-eyebrow">SAVE Intelligence</span>
                <h2>Network security copilot</h2>
              </div>
              <FaRobot className="nsc-heading-icon" />
            </div>
            <div className="nsc-ai-status">
              <span>Network security status</span>
              <strong>{selectedScan ? `${counts.total} findings require review` : "Run the first assessment to establish a baseline"}</strong>
            </div>
            <div className="nsc-recommendation">
              <FaBolt />
              <div>
                <span>Today&apos;s recommendation</span>
                <strong>{topFinding ? titleOf(topFinding) : "Establish a trusted network baseline"}</strong>
                <p>{topFinding ? `Review the ${severityOf(topFinding)}-severity evidence and validate the affected service before remediation.` : "Run an authorized public or private IP scan, then prioritize the verified findings."}</p>
              </div>
            </div>
            <div className="nsc-findings-board">
              <div className="nsc-findings-tabs" role="tablist" aria-label="Network finding views">
                <button
                  id="nsc-prioritized-tab"
                  className={findingsView === "prioritized" ? "nsc-findings-tab nsc-findings-tab--active" : "nsc-findings-tab"}
                  type="button"
                  role="tab"
                  aria-selected={findingsView === "prioritized"}
                  aria-controls="nsc-findings-panel"
                  onClick={() => setFindingsView("prioritized")}
                >
                  <span>Prioritized findings</span>
                  <strong>{priorityQueue.length}</strong>
                </button>
                <button
                  id="nsc-all-tab"
                  className={findingsView === "all" ? "nsc-findings-tab nsc-findings-tab--active" : "nsc-findings-tab"}
                  type="button"
                  role="tab"
                  aria-selected={findingsView === "all"}
                  aria-controls="nsc-findings-panel"
                  onClick={() => setFindingsView("all")}
                >
                  <span>All findings</span>
                  <strong>{allFindingRows.length}</strong>
                </button>
              </div>

              <section
                id="nsc-findings-panel"
                className="nsc-findings-column"
                role="tabpanel"
                aria-labelledby={findingsView === "prioritized" ? "nsc-prioritized-tab" : "nsc-all-tab"}
              >
                <div className="nsc-findings-header">
                  <div>
                    <span>{findingsView === "prioritized" ? "Immediate remediation queue" : "Complete assessment evidence"}</span>
                    <small>
                      {findingsView === "prioritized"
                        ? `Top ${priorityQueue.length} by severity, exploitability and impact`
                        : "Every finding reported by the selected assessment"}
                    </small>
                  </div>
                  <strong>{findingsView === "prioritized" ? priorityQueue.length : allFindingRows.length}</strong>
                </div>
                {findingsView === "prioritized" ? (
                  priorityQueue.length ? (
                    <div className="nsc-findings-list" aria-label="Highest-priority network findings">
                      {priorityQueue.map((finding, index) =>
                        renderFindingAction(finding, index, "priority")
                      )}
                    </div>
                  ) : <EmptyState>No priority finding evidence is available yet.</EmptyState>
                ) : (
                  allFindingRows.length ? (
                    <div className="nsc-findings-list" aria-label="All network findings">
                      {allFindingRows.map(({ finding, priorityIndex }) =>
                        renderFindingAction(finding, priorityIndex, "all")
                      )}
                    </div>
                  ) : <EmptyState>No finding evidence is available yet.</EmptyState>
                )}
              </section>
            </div>
            <div className="nsc-ai-questions">
              {["Is my network secure?", "What should I fix today?", "Show exposed ports.", "Which assets are critical?"].map((question) => (
                <button key={question} type="button" onClick={() => askAi(question)}>{question}</button>
              ))}
            </div>
            <button className="nsc-ai-input" type="button" onClick={() => askAi("Analyze my current network security posture and recommend the next actions.")}>
              <span>Ask SAVE AI about this network</span><FaRobot />
            </button>
          </article>

          <aside className="nsc-card nsc-intel-card">
            <div className="nsc-section-heading">
              <div><span className="nsc-eyebrow">Threat Intelligence</span><h2>Exposure watch</h2></div>
            </div>
            <div className="nsc-intel-group">
              <h3>Latest CVEs</h3>
              {verifiedCves.length ? verifiedCves.map((row) => (
                <div className="nsc-intel-row" key={cveOf(row)}>
                  <span>{cveOf(row)}</span>
                  <strong>EPSS {row.epss ?? row.EPSS ?? "—"}</strong>
                </div>
              )) : <EmptyState>No verified CVE intelligence in this scan.</EmptyState>}
            </div>
            <div className="nsc-intel-group">
              <h3>Sensitive exposed services</h3>
              {sensitiveServices.length ? sensitiveServices.map((service, index) => (
                <div className="nsc-intel-row" key={`${servicePort(service)}-${index}`}>
                  <span>Port {servicePort(service)}</span>
                  <strong>{serviceName(service)}</strong>
                </div>
              )) : <EmptyState>No sensitive-port evidence was reported.</EmptyState>}
            </div>
            <div className="nsc-intel-group">
              <h3>KEV &amp; campaign alerts</h3>
              <EmptyState>No KEV or live threat-feed API is connected.</EmptyState>
            </div>
          </aside>
        </section>

        <section className="nsc-card nsc-actions-card">
          <div className="nsc-section-heading">
            <div><span className="nsc-eyebrow">Approved Workflows</span><h2>Quick actions</h2></div>
          </div>
          <div className="nsc-actions">
            <button type="button" onClick={() => openScan("public")}><FaGlobe /><span>Scan Public IP</span></button>
            <button type="button" onClick={() => openScan("private")}><FaShieldAlt /><span>Scan Private IP</span></button>
            <button type="button" onClick={() => openScan("network")}><FaNetworkWired /><span>Network Scan</span></button>
            <button type="button" onClick={openAgents}><FaPlus /><span>Add Agent</span></button>
            <button type="button" disabled title="No asset import API is connected"><FaDatabase /><span>Import Assets</span><small>Coming soon</small></button>
            <Link to="/dashboard/reports"><FaFileAlt /><span>View Reports</span></Link>
          </div>
        </section>

        {selectedScan && (
          <section className="nsc-evidence-grid">
            <article className="nsc-card nsc-coverage-card">
              <div className="nsc-section-heading">
                <div>
                  <span className="nsc-eyebrow">Assessment Evidence</span>
                  <h2>Coverage and limitations</h2>
                </div>
                <span className={`nsc-coverage-badge nsc-coverage-badge--${selectedScan.scan_status || 'completed'}`}>
                  {selectedScan.scan_profile || 'legacy'} · {selectedScan.score_status === 'not_calculated' ? 'score withheld' : selectedScan.scan_status || 'completed'}
                </span>
              </div>
              {coverageRows.length ? (
                <div className="nsc-coverage-list">
                  {coverageRows.map(([key, item]) => (
                    <div className="nsc-coverage-row" key={key}>
                      <i className={`nsc-coverage-status nsc-coverage-status--${item.status}`} />
                      <div>
                        <strong>{item.label || key.replaceAll('_', ' ')}</strong>
                        <span>{item.details || 'No details reported'}</span>
                        {item.reason && <small>{item.reason}</small>}
                      </div>
                      <b>{String(item.status).replaceAll('_', ' ')}</b>
                    </div>
                  ))}
                </div>
              ) : <EmptyState>This older scan did not report coverage metadata.</EmptyState>}
              {diagnostics.length > 0 && (
                <div className="nsc-diagnostics">
                  <strong>Scanner diagnostics</strong>
                  {diagnostics.slice(0, 6).map((item, index) => (
                    <span key={`${item.stage || 'diagnostic'}-${index}`}>
                      <FaExclamationTriangle /> {item.message || item.detail || String(item)}
                    </span>
                  ))}
                </div>
              )}
            </article>

            <article className="nsc-card nsc-handoff-card">
              <div className="nsc-section-heading">
                <div><span className="nsc-eyebrow">Follow-up Workflows</span><h2>Change and web handoff</h2></div>
              </div>
              <div className="nsc-comparison">
                <h3>Since previous assessment</h3>
                {comparison ? (
                  <div>
                    <span>Findings <strong>{comparison.finding_delta > 0 ? '+' : ''}{comparison.finding_delta}</strong></span>
                    <span>Critical <strong>{comparison.critical_delta > 0 ? '+' : ''}{comparison.critical_delta}</strong></span>
                    <span>Services <strong>{comparison.service_delta > 0 ? '+' : ''}{comparison.service_delta}</strong></span>
                    <span>Score <strong>{comparison.score_delta == null ? '—' : `${comparison.score_delta > 0 ? '+' : ''}${comparison.score_delta}`}</strong></span>
                  </div>
                ) : <EmptyState>No earlier comparable scan is available.</EmptyState>}
              </div>
              <div className="nsc-web-candidates">
                <h3>Discovered web services</h3>
                {webCandidates.length ? webCandidates.slice(0, 8).map((candidate, index) => (
                  <div key={`${candidate.url || candidate.host || 'web'}-${index}`}>
                    <span><FaGlobe /> {candidate.url || `${candidate.host || selectedScan.ip_address}:${candidate.port || ''}`}</span>
                    <Link to="/save/dashboard/web-dashboard">Open Web Dashboard</Link>
                  </div>
                )) : <EmptyState>No HTTP(S) candidates were discovered in this assessment.</EmptyState>}
                <small>Opening the Web Dashboard does not start a scan. Confirm target ownership before submitting it.</small>
              </div>
            </article>
          </section>
        )}

        <section className="nsc-insight-grid">
          <article className="nsc-card nsc-chart-card">
            <div className="nsc-section-heading">
              <div><span className="nsc-eyebrow">Historical Intelligence</span><h2>Security posture trend</h2></div>
              <FaHistory className="nsc-heading-icon" />
            </div>
            <p className="nsc-section-copy">SAVE scores from completed network scans. A higher score represents a stronger posture.</p>
            <div className="nsc-chart-wrap">
              {trendData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 14, right: 14, left: -24, bottom: 0 }}>
                    <defs><linearGradient id="nscScoreGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#5de1ff" stopOpacity={0.58}/><stop offset="100%" stopColor="#5de1ff" stopOpacity={0.02}/></linearGradient></defs>
                    <CartesianGrid stroke="#203453" strokeDasharray="3 6" />
                    <XAxis dataKey="scan" stroke="#7790b2" tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 10]} stroke="#7790b2" tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#091429", border: "1px solid #294465", borderRadius: 12 }} />
                    <Area type="monotone" dataKey="score" stroke="#5de1ff" strokeWidth={3} fill="url(#nscScoreGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <EmptyState>Your trend starts after the first completed scan.</EmptyState>}
            </div>
          </article>

          <article className="nsc-card nsc-risk-card">
            <div className="nsc-section-heading"><div><span className="nsc-eyebrow">Risk Profile</span><h2>Finding distribution</h2></div></div>
            <div className="nsc-risk-content">
              <div className="nsc-pie-wrap">
                {hasRiskData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={riskData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={4}>{riskData.map((entry, index) => <Cell key={entry.name} fill={RISK_COLORS[index]} />)}</Pie><Tooltip contentStyle={{ background: "#091429", border: "1px solid #294465", borderRadius: 12 }} /></PieChart>
                  </ResponsiveContainer>
                ) : <EmptyState>No scored findings yet.</EmptyState>}
              </div>
              <div className="nsc-risk-legend">
                {riskData.map((item, index) => <div key={item.name}><i style={{ background: RISK_COLORS[index] }} /><span>{item.name}</span><strong>{item.value}</strong></div>)}
              </div>
            </div>
          </article>
        </section>

        <section className="nsc-lower-grid">
          <article className="nsc-card">
            <div className="nsc-section-heading"><div><span className="nsc-eyebrow">Asset Summary</span><h2>Observed platforms</h2></div></div>
            <div className="nsc-os-grid">
              {osCards.map((item) => <div key={item.id}><span>{item.icon}</span><strong>{item.label}</strong><b>{item.count}</b><small>Observed assets</small></div>)}
            </div>
            {assetStats.total > 0 && assetStats.os.unknown === assetStats.total && <p className="nsc-data-note">The current network API does not report operating-system metadata, so observed assets remain classified as Unknown.</p>}
          </article>

          <article className="nsc-card">
            <div className="nsc-section-heading"><div><span className="nsc-eyebrow">Operational Timeline</span><h2>Recent activity</h2></div></div>
            <div className="nsc-timeline">
              {results.length ? results.slice(-5).reverse().map((scan, index) => (
                <div key={scan.scan_uuid || index}><i><FaCheckCircle /></i><div><strong>Network scan {scan.scan_status === 'partial' ? 'completed partially' : 'completed'}</strong><span>{dateLabel(scan.created_at, true)} · {numberOr(scan.t_vul)} findings · score {scan.score_status === 'not_calculated' || scan.ss == null ? 'not calculated' : clampScore(scan.ss).toFixed(1)}</span></div></div>
              )) : <EmptyState>No completed network activity is available.</EmptyState>}
            </div>
          </article>
        </section>

        <footer className="nsc-footer-note">
          <FaShieldAlt /> Dashboard metrics come from existing SAVE network scan records. Unconnected intelligence sources are never simulated.
        </footer>
      </div>
    </div>
  );
};

export default NetworkSecurityCenter;
